import React, { memo } from 'react';

import { TONE_CRITICAL, TONE_DEFAULT, TONE_MUTED } from './dependencyArrowGeometry';

// Rows either side of the rendered range whose arrows are still drawn, so an arrow does not pop in
// only once its end row scrolls into view.
const ROW_BUFFER = 5;

const TONES = {
    [TONE_DEFAULT]: { markerId: 'project-gantt-arrowhead', className: 'tw-text-charcoal-50', strokeWidth: 1.4 },
    [TONE_MUTED]: { markerId: 'project-gantt-arrowhead-muted', className: 'tw-text-charcoal-25', strokeWidth: 1.4 },
    [TONE_CRITICAL]: { markerId: 'project-gantt-arrowhead-critical', className: 'tw-text-red', strokeWidth: 1.8 },
};

// A filled triangle 7px long and 8px tall whose tip lands on the successor's edge. Sized in user
// space so it does not grow with the stroke width.
const Arrowhead = ({ id, className }) => (
    <marker id={id} className={className} viewBox="0 0 7 8" refX="7" refY="4" markerWidth="7" markerHeight="8" markerUnits="userSpaceOnUse" orient="auto">
        <path d="M0,0L7,4L0,8z" fill="currentColor" />
    </marker>
);

/**
 * The dependency arrows over the Gantt's scrollable grid, drawn inside its scrolling content so
 * they move with the bars. Only arrows crossing the rendered rows are drawn. Display only:
 * `pointer-events: none` keeps every bar clickable underneath.
 */
const DependencyArrows = ({ arrows, rowStartIndex, rowStopIndex, width, height, left, top }) => {
    const first = rowStartIndex - ROW_BUFFER;
    const last = rowStopIndex + ROW_BUFFER;
    // Critical arrows last, so they are drawn over the muted ones they cross.
    const visible = arrows
        .filter((arrow) => arrow.rowEnd >= first && arrow.rowStart <= last)
        .sort((a, b) => (a.tone === TONE_CRITICAL) - (b.tone === TONE_CRITICAL));

    // Beneath the bars (z-20): the key-milestone diamond sits inside a bar's own stacking context,
    // so anything raised above the bars would also paint over the diamond.
    return (
        <svg
            className="tw-absolute tw-z-10 tw-pointer-events-none tw-overflow-visible"
            style={{ left, top, width, height }}
            width={width}
            height={height}
            aria-hidden="true"
        >
            <defs>
                {Object.values(TONES).map((tone) => (
                    <Arrowhead key={tone.markerId} id={tone.markerId} className={tone.className} />
                ))}
            </defs>
            <g fill="none">
                {visible.map((arrow) => {
                    const tone = TONES[arrow.tone];

                    return (
                        <path
                            key={arrow.key}
                            d={arrow.d}
                            stroke="currentColor"
                            strokeWidth={tone.strokeWidth}
                            className={tone.className}
                            markerEnd={`url(#${tone.markerId})`}
                        />
                    );
                })}
            </g>
        </svg>
    );
};

export default memo(DependencyArrows);
